import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Clock3,
  MessageSquare,
  Send,
  ArrowRight,
  ShieldCheck,
  Headphones,
  Building2,
  CheckCircle2,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);

  const contactOptions = [
    {
      icon: MessageSquare,
      title: "General Enquiries",
      description:
        "Have a question about Doxzon? Send us a message and our team can help.",
      action: "Send an enquiry",
    },
    {
      icon: Headphones,
      title: "Support",
      description:
        "Need help with a Doxzon service or experience? Our support team is here to help.",
      action: "Contact Support",
    },
    {
      icon: Building2,
      title: "Business Enquiries",
      description:
        "Interested in partnering with Doxzon or learning more about our platform?",
      action: "Talk to our team",
    },
  ];

  const faqs = [
    {
      question: "How can I contact Doxzon?",
      answer:
        "You can use the contact form on this page to send your enquiry to the appropriate Doxzon team.",
    },
    {
      question: "Where can I get technical support?",
      answer:
        "For product or technical questions, use the support option and provide as much relevant information as possible.",
    },
    {
      question: "Can I contact Doxzon about a partnership?",
      answer:
        "Yes. Business and partnership enquiries can be submitted through the contact form.",
    },
    {
      question: "Can I ask about careers?",
      answer:
        "For career opportunities, visit the Careers section to explore available positions.",
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-28">

            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl"
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Contact Doxzon
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[64px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Let's start a
                <span className="block text-teal-500 mt-2">
                  conversation.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-2xl">
                Whether you have a question, need support, or want to explore
                working with Doxzon, we'd love to hear from you.
              </p>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            CONTACT FORM + INFO
        ====================================================== */}
        <section className="py-20 md:py-24">

          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.25fr_0.75fr] gap-8">

            {/* FORM */}
            <motion.div
              initial={{ opacity: 0, x: -25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-[30px] border border-slate-100 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-7 md:p-10"
            >

              {!submitted ? (
                <>
                  <div>

                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-600">
                      Send a Message
                    </span>

                    <h2 className="font-sora text-3xl md:text-4xl font-medium mt-3">
                      How can we help?
                    </h2>

                    <p className="text-slate-500 leading-7 mt-4">
                      Tell us a little about what you need and we'll make sure
                      your enquiry reaches the right team.
                    </p>

                  </div>

                  <form
                    onSubmit={handleSubmit}
                    className="mt-8 space-y-5"
                  >

                    {/* NAME + EMAIL */}
                    <div className="grid md:grid-cols-2 gap-5">

                      <div>

                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Full Name
                        </label>

                        <input
                          type="text"
                          required
                          placeholder="Your name"
                          className="w-full h-12 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-teal-400 focus:ring-4 focus:ring-teal-50 transition"
                        />

                      </div>

                      <div>

                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Email Address
                        </label>

                        <input
                          type="email"
                          required
                          placeholder="you@example.com"
                          className="w-full h-12 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-teal-400 focus:ring-4 focus:ring-teal-50 transition"
                        />

                      </div>

                    </div>

                    {/* PHONE + SUBJECT */}
                    <div className="grid md:grid-cols-2 gap-5">

                      <div>

                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Phone Number
                        </label>

                        <input
                          type="tel"
                          placeholder="Your phone number"
                          className="w-full h-12 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-teal-400 focus:ring-4 focus:ring-teal-50 transition"
                        />

                      </div>

                      <div>

                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Enquiry Type
                        </label>

                        <select
                          required
                          className="w-full h-12 rounded-xl border border-slate-200 px-4 text-sm outline-none bg-white focus:border-teal-400 focus:ring-4 focus:ring-teal-50 transition"
                        >

                          <option value="">
                            Select an option
                          </option>

                          <option>
                            General Enquiry
                          </option>

                          <option>
                            Technical Support
                          </option>

                          <option>
                            Business Enquiry
                          </option>

                          <option>
                            Partnership
                          </option>

                          <option>
                            Careers
                          </option>

                        </select>

                      </div>

                    </div>

                    {/* MESSAGE */}
                    <div>

                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Message
                      </label>

                      <textarea
                        required
                        rows="6"
                        placeholder="Tell us how we can help..."
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none resize-none focus:border-teal-400 focus:ring-4 focus:ring-teal-50 transition"
                      />

                    </div>

                    {/* CONSENT */}
                    <label className="flex items-start gap-3 cursor-pointer">

                      <input
                        type="checkbox"
                        required
                        className="mt-1 accent-teal-500"
                      />

                      <span className="text-xs text-slate-500 leading-5">
                        I understand that my information will be used to
                        respond to my enquiry.
                      </span>

                    </label>

                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 w-full md:w-auto rounded-xl bg-slate-950 text-white px-7 h-12 text-sm font-semibold hover:bg-teal-500 transition"
                    >

                      Send Message

                      <Send size={16} />

                    </button>

                  </form>
                </>
              ) : (
                /* SUCCESS */
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="min-h-[500px] flex flex-col items-center justify-center text-center"
                >

                  <div className="w-16 h-16 rounded-2xl bg-teal-50 grid place-items-center">

                    <CheckCircle2
                      size={32}
                      className="text-teal-600"
                    />

                  </div>

                  <h2 className="font-sora text-3xl font-semibold mt-6">
                    Message received
                  </h2>

                  <p className="text-slate-500 leading-7 max-w-md mt-4">
                    Thanks for getting in touch with Doxzon. Your message has
                    been submitted successfully.
                  </p>

                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-7 text-sm font-semibold text-teal-600 hover:text-teal-700"
                  >
                    Send another message
                  </button>

                </motion.div>
              )}

            </motion.div>

            {/* CONTACT INFORMATION */}
            <motion.div
              initial={{ opacity: 0, x: 25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-5"
            >

              {/* INFO CARD */}
              <div className="rounded-[30px] bg-slate-950 text-white p-7 md:p-8">

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-400">
                  Get In Touch
                </span>

                <h2 className="font-sora text-2xl md:text-3xl font-medium mt-4">
                  We're here to help.
                </h2>

                <p className="text-slate-400 leading-7 mt-4">
                  Choose the best way to reach us based on what you need.
                </p>

                <div className="space-y-5 mt-8">

                  <div className="flex gap-4">

                    <div className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center shrink-0">

                      <Mail
                        size={18}
                        className="text-teal-400"
                      />

                    </div>

                    <div>

                      <p className="text-xs text-slate-500 uppercase tracking-wider">
                        Email
                      </p>

                      <p className="text-sm font-medium mt-1">
                        Your official Doxzon email
                      </p>

                    </div>

                  </div>

                  <div className="flex gap-4">

                    <div className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center shrink-0">

                      <Phone
                        size={18}
                        className="text-teal-400"
                      />

                    </div>

                    <div>

                      <p className="text-xs text-slate-500 uppercase tracking-wider">
                        Phone
                      </p>

                      <p className="text-sm font-medium mt-1">
                        Your official Doxzon phone number
                      </p>

                    </div>

                  </div>

                  <div className="flex gap-4">

                    <div className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center shrink-0">

                      <MapPin
                        size={18}
                        className="text-teal-400"
                      />

                    </div>

                    <div>

                      <p className="text-xs text-slate-500 uppercase tracking-wider">
                        Location
                      </p>

                      <p className="text-sm font-medium mt-1">
                        Your official Doxzon office location
                      </p>

                    </div>

                  </div>

                </div>

              </div>

              {/* HOURS */}
              <div className="rounded-3xl border border-slate-100 bg-slate-50 p-7">

                <div className="w-11 h-11 rounded-xl bg-white grid place-items-center">

                  <Clock3
                    size={19}
                    className="text-teal-600"
                  />

                </div>

                <h3 className="font-sora text-lg font-semibold mt-5">
                  Support Hours
                </h3>

                <p className="text-slate-500 text-sm leading-7 mt-2">
                  Add your official support hours here so visitors know when
                  they can expect a response.
                </p>

              </div>

              {/* SECURITY */}
              <div className="rounded-3xl border border-teal-100 bg-teal-50 p-7">

                <div className="flex items-start gap-4">

                  <div className="w-11 h-11 rounded-xl bg-white grid place-items-center shrink-0">

                    <ShieldCheck
                      size={19}
                      className="text-teal-600"
                    />

                  </div>

                  <div>

                    <h3 className="font-semibold">
                      Your privacy matters
                    </h3>

                    <p className="text-teal-800/70 text-sm leading-6 mt-2">
                      Please avoid including sensitive medical or personal
                      information unless specifically requested through an
                      appropriate secure channel.
                    </p>

                  </div>

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            CONTACT OPTIONS
        ====================================================== */}
        <section className="py-24 bg-[#f6fbfc]">

          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-600">
                How Can We Help?
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Choose what you need.
              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Send your question to the right team and we'll help you move
                forward.
              </p>

            </div>

            <div className="grid md:grid-cols-3 gap-5 mt-12">

              {contactOptions.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -6 }}
                    className="group rounded-3xl bg-white border border-slate-100 p-7 hover:border-teal-100 hover:shadow-xl transition"
                  >

                    <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">

                      <Icon
                        size={22}
                        className="text-teal-600 group-hover:text-white transition"
                      />

                    </div>

                    <h3 className="font-sora text-xl font-semibold mt-6">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-7 mt-3">
                      {item.description}
                    </p>

                    <button className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold mt-6">

                      {item.action}

                      <ArrowRight size={15} />

                    </button>

                  </motion.div>

                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">

              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-600">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Before you reach out.
              </h2>

            </div>

            <div className="space-y-3 mt-12">

              {faqs.map((faq) => (

                <details
                  key={faq.question}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                >

                  <summary className="cursor-pointer list-none flex items-center justify-between gap-5 p-6 font-sora font-semibold">

                    <span>
                      {faq.question}
                    </span>

                    <span className="w-8 h-8 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">

                      <ArrowRight
                        size={15}
                        className="group-open:rotate-90 transition-transform"
                      />

                    </span>

                  </summary>

                  <p className="px-6 pb-6 text-slate-600 leading-7 text-sm">
                    {faq.answer}
                  </p>

                </details>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-400">
                Doxzon
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Have something to
                <span className="text-teal-400">
                  {" "}talk about?
                </span>

              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                We're always interested in hearing questions, ideas and
                opportunities that can help improve connected healthcare.
              </p>

              <button
                onClick={() =>
                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  })
                }
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition"
              >

                Send an Enquiry

                <ArrowRight size={17} />

              </button>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Contact;